/**
 * Pure card-state helpers for the OMEGA cockpit (S45).
 *
 * Each card maps a raw endpoint response (or an error) to a deterministic
 * `{ status, sentence }` — a green/yellow/red dot + ONE human English sentence.
 * Defensive on the input shape: an unexpected/absent field degrades to a safe
 * generic sentence rather than crashing (the cockpit must never blank out).
 *
 * Exported pure so they are unit-tested without rendering React.
 */

export type CardStatus = 'green' | 'yellow' | 'red' | 'unknown';

export interface CardState {
  status: CardStatus;
  sentence: string;
}

/** Dot colour per status (white-theme friendly). */
export const STATUS_COLORS: Record<CardStatus, string> = {
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
  unknown: '#9ca3af',
};

// ── Defensive field readers ───────────────────────────────────────────────────

function asRecord(raw: unknown): Record<string, unknown> | undefined {
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
}

/** A numeric field, or `undefined`. */
export function numField(raw: unknown, key: string): number | undefined {
  const v = asRecord(raw)?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Length of an array field (the field itself, or `undefined`). */
export function lenField(raw: unknown, key: string): number | undefined {
  const v = asRecord(raw)?.[key];
  return Array.isArray(v) ? v.length : undefined;
}

/** Length when the response is itself an array, else `undefined`. */
export function arrLen(raw: unknown): number | undefined {
  return Array.isArray(raw) ? raw.length : undefined;
}

/** The standard "couldn't load" card state. */
const UNAVAILABLE: CardState = { status: 'unknown', sentence: 'Status unavailable.' };

/**
 * Business cards — the /omega/business summary endpoints already compute the
 * status + sentence server-side, so the card just reads them (defensively). An
 * unrecognised status degrades to "unknown".
 */
export function businessCard(raw: unknown, isError: boolean): CardState {
  if (isError) return UNAVAILABLE;
  const rec = asRecord(raw);
  const status = rec?.['status'];
  const sentence = rec?.['sentence'];
  const validStatus: CardStatus =
    status === 'green' || status === 'yellow' || status === 'red' ? status : 'unknown';
  return {
    status: validStatus,
    sentence: typeof sentence === 'string' && sentence.length > 0 ? sentence : 'Status unavailable.',
  };
}

// ── System card mappers ───────────────────────────────────────────────────────

/** Monitor — `GET /admin/fingerprints`. */
export function monitorCard(raw: unknown, isError: boolean): CardState {
  if (isError) return UNAVAILABLE;
  const n = lenField(raw, 'fingerprints') ?? arrLen(raw);
  if (n === undefined) return { status: 'green', sentence: 'Monitoring active.' };
  return { status: 'green', sentence: `Monitoring ${n} target${n === 1 ? '' : 's'}, no anomalies.` };
}

/** Loop (S49, W2) — status read may not exist yet. */
export function loopCard(raw: unknown, isError: boolean): CardState {
  if (isError) return { status: 'unknown', sentence: 'Loop status pending (S49).' };
  const state = asRecord(raw)?.['state'];
  if (state === 'paused') return { status: 'yellow', sentence: 'The Perfect Loop is paused.' };
  if (state === 'stalled') return { status: 'red', sentence: 'The Perfect Loop is stalled.' };
  return { status: 'green', sentence: 'The Perfect Loop is running.' };
}

/** DLM (S50) — `GET /omega/dlm/summary`. */
export function dlmCard(raw: unknown, isError: boolean): CardState {
  if (isError) return { status: 'unknown', sentence: 'DLM not yet deployed.' };
  const total = numField(raw, 'total_compositions') ?? 0;
  const det = numField(raw, 'determinism_pct') ?? 100;
  const trace = numField(raw, 'traceability_pct') ?? 100;
  if (det >= 100 && trace >= 100) {
    return { status: 'green', sentence: `Composed ${total} output${total === 1 ? '' : 's'}, 100% traceable.` };
  }
  return { status: 'yellow', sentence: `Quality below target (determinism ${det}%, traceability ${trace}%).` };
}

/** Decomposition (S48) — `GET /admin/decomposition/summary`. */
export function decompositionCard(raw: unknown, isError: boolean): CardState {
  if (isError) return UNAVAILABLE;
  const atoms = numField(raw, 'total_atoms') ?? numField(raw, 'atoms') ?? lenField(raw, 'atoms');
  if (atoms === undefined) return { status: 'green', sentence: 'Decomposition engine ready.' };
  if (atoms === 0) return { status: 'yellow', sentence: 'No atoms decomposed yet.' };
  return { status: 'green', sentence: `Decomposed ${atoms} atoms into the lattice.` };
}

/** Discovery Labs — `GET /omega/brain/discovery/outputs`. */
export function discoveryCard(raw: unknown, isError: boolean): CardState {
  if (isError) return UNAVAILABLE;
  const n = arrLen(raw) ?? lenField(raw, 'outputs') ?? 0;
  if (n === 0) return { status: 'yellow', sentence: 'No recent lab outputs.' };
  return { status: 'green', sentence: `${n} recent discovery lab output${n === 1 ? '' : 's'}.` };
}

/** Research Teams — `GET /admin/research/conductors`. */
export function researchCard(raw: unknown, isError: boolean): CardState {
  if (isError) return UNAVAILABLE;
  const conductors = Array.isArray(raw) ? raw : [];
  const running = conductors.filter(
    (c) => asRecord(c)?.['status'] === 'Running',
  ).length;
  if (conductors.length === 0) return { status: 'yellow', sentence: 'No research teams active.' };
  if (running === 0) return { status: 'yellow', sentence: `${conductors.length} research team${conductors.length === 1 ? '' : 's'}, none running.` };
  return { status: 'green', sentence: `${running} of ${conductors.length} research teams running.` };
}

/** Budget — `GET /admin/budget/summary` (spent vs A-02 ceiling). */
export function budgetCard(raw: unknown, isError: boolean): CardState {
  if (isError) return UNAVAILABLE;
  const spent = numField(raw, 'spent_month_usd_cents') ?? 0;
  const ceiling = numField(raw, 'ceiling_month_usd_cents') ?? 0;
  const degradation = asRecord(raw)?.['degradation'];
  const status: CardStatus =
    degradation === 'hard_stop' ? 'red' : degradation === 'degraded' ? 'yellow' : 'green';
  const dollars = (c: number) => `$${(c / 100).toFixed(2)}`;
  const sentence =
    ceiling > 0
      ? `${dollars(spent)} of ${dollars(ceiling)} budget used (${Math.round((spent / ceiling) * 100)}%).`
      : `${dollars(spent)} spent this month; no ceiling set.`;
  return { status, sentence };
}

/**
 * Knowledge Gates — `GET /admin/learning/gates`. The endpoint returns the V-026
 * gate state as `{ gate: { g1_…: bool, …, all_gates_open: bool }, … }` (the page
 * reads `gate.all_gates_open`). Count the boolean `g*` gates; fall back to the
 * older open/blocked-count shape if present.
 */
export function knowledgeGatesCard(raw: unknown, isError: boolean): CardState {
  if (isError) return UNAVAILABLE;
  const rec = asRecord(raw);
  const gate = asRecord(rec?.['gate']) ?? rec;
  if (gate) {
    const gateKeys = Object.keys(gate).filter(
      (k) => k.startsWith('g') && typeof gate[k] === 'boolean',
    );
    if (gateKeys.length > 0) {
      const total = gateKeys.length;
      const openCount = gateKeys.filter((k) => gate[k] === true).length;
      if (gate['all_gates_open'] === true || openCount === total) {
        return { status: 'green', sentence: `All ${total} knowledge gates open (V-026 ready).` };
      }
      return { status: 'yellow', sentence: `${openCount} of ${total} knowledge gates open.` };
    }
  }
  const open = numField(raw, 'open') ?? numField(raw, 'green');
  const blocked = numField(raw, 'blocked') ?? numField(raw, 'red');
  if (blocked !== undefined && blocked > 0) {
    return { status: 'red', sentence: `${blocked} knowledge gate${blocked === 1 ? '' : 's'} blocked.` };
  }
  if (open !== undefined) return { status: 'green', sentence: `${open} knowledge gate${open === 1 ? '' : 's'} open.` };
  return { status: 'green', sentence: 'Knowledge gates nominal.' };
}
